import { useEffect, useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { AlertCircle, ArrowRight, Check, CheckCircle2, Clock3, Files, PenLine } from 'lucide-react';
import { toast } from 'sonner';
import { ActionBar, BarButton } from '@generated/components/form/ActionBar';
import { AttachmentRetry } from '@generated/components/form/AttachmentRetry';
import { DeclarationDocument } from '@generated/components/form/DeclarationDocument';
import { DeclarationEditor } from '@generated/components/form/DeclarationEditor';
import { SubmittedReceipt } from '@generated/components/form/SubmittedReceipt';
import { TurnstileWidget } from '@generated/components/TurnstileWidget';
import { useDeclarationForm } from '@generated/hooks/useDeclarationForm';
import { academicYearTitle } from '@generated/lib/academicYear';
import { checkDeclarationFiles, submitDeclaration, uploadDeclarationFile } from '@generated/server/declaration';

export const Route = createFileRoute('/_app/forms/military-declaration')({
  component: MilitaryDeclarationPage,
});

type Mode = 'edit' | 'review' | 'submitted';

function makeReference(): string {
  const now = new Date();
  const stamp = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}`;
  const suffix = String(now.getHours()).padStart(2, '0') + String(now.getMinutes()).padStart(2, '0');
  return `MIL-${stamp}-${suffix}`;
}

function dataUrlToFile(dataUrl: string, filename: string): File {
  const [header, base64] = dataUrl.split(',');
  const mimeMatch = header.match(/data:(.*);base64/);
  const mime = mimeMatch?.[1] ?? 'image/png';
  const binary = atob(base64 ?? '');
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return new File([bytes], filename, { type: mime });
}

type UploadTarget = { kind: 'signature' | 'form3010'; label: string; file: File };
type UploadOutcome = { placement: 'column' | 'update' | 'failed'; reason?: string; verified?: boolean };

// One attachment, two attempts. Returns where the file landed instead of throwing, so one missing
// attachment never hides the other and never rolls back a submission that already succeeded.
async function sendAttachment(target: UploadTarget, itemId: string): Promise<UploadOutcome> {
  let reason = 'ההעלאה נכשלה';
  for (let attempt = 1; attempt <= 2; attempt += 1) {
    try {
      const form = new FormData();
      form.append('file', target.file);
      form.append('itemId', itemId);
      form.append('kind', target.kind);
      const res = await uploadDeclarationFile({ data: form });
      return { placement: res?.placement === 'update' ? 'update' : 'column', verified: res?.verified === true };
    } catch (err) {
      console.error(`Failed to upload ${target.kind} (attempt ${attempt})`, err);
      reason = err instanceof Error ? err.message : String(err);
      if (attempt < 2) await new Promise((resolve) => setTimeout(resolve, 800));
    }
  }
  return { placement: 'failed', reason };
}

function MilitaryDeclarationPage() {
  const api = useDeclarationForm();
  const [mode, setMode] = useState<Mode>('edit');
  const [reference, setReference] = useState('');
  const [itemId, setItemId] = useState<string | null>(null);
  const [form3010Files, setForm3010Files] = useState<File[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  // Attachments that did not make it onto the item. The item exists, so these can be re-sent alone.
  const [pending, setPending] = useState<UploadTarget[]>([]);
  const [retrying, setRetrying] = useState(false);
  const [checking, setChecking] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);

  const dirty = Boolean(api.values.employeeName || api.values.employeeId || api.values.periods[0]?.from);

  useEffect(() => {
    if (mode !== 'edit' || !dirty) return;
    const handler = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty, mode]);

  const goToReview = () => {
    setMode('review');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const submit = async () => {
    setSubmitting(true);
    setSubmitError(null);
    try {
      const res = await submitDeclaration({
        data: {
          employeeName: api.values.employeeName,
          employeeId: api.values.employeeId,
          employeeEmail: api.values.employeeEmail,
          department: api.values.department,
          statementMonths: api.values.statementMonths,
          statementYear: api.values.statementYear,
          reportsNum: api.values.reportsNum,
          coursesNum: api.values.coursesNum,
          periods: api.values.periods,
          courses: api.values.courses,
          employeeSignatureDate: api.values.employeeSignatureDate,
          turnstileToken: turnstileToken || undefined,
        },
      });
      const newItemId = res?.itemId;
      if (!newItemId) throw new Error('לא התקבל מזהה פריט מהלוח');

      // Files go up ONE AT A TIME. Two concurrent file mutations on the same board item collide and
      // monday drops one of them, so parallel uploads lose the second file. Each attempt is wrapped
      // on its own so a failure attaching one file never skips the other, and never aborts the
      // submission — the board item already exists at this point.
      const failedUploads: string[] = [];
      const relocatedUploads: string[] = [];
      const targets: UploadTarget[] = [];

      // Add all attached 3010 files (up to 4)
      form3010Files.forEach((f, idx) => {
        targets.push({
          kind: 'form3010',
          label: form3010Files.length > 1 ? `קובץ טופס 3010 (${idx + 1}/${form3010Files.length})` : 'קובץ טופס 3010',
          file: f,
        });
      });

      if (api.values.signature) {
        targets.push({
          kind: 'signature',
          label: 'החתימה',
          file: dataUrlToFile(api.values.signature, 'employee-signature.png'),
        });
      } else {
        failedUploads.push('החתימה — לא נשמרה בטופס, יש לחתום מחדש ולשלוח שוב');
      }

      const stillMissing: UploadTarget[] = [];
      const unconfirmed: string[] = [];
      for (const target of targets) {
        const outcome = await sendAttachment(target, newItemId);
        // Accepted by the board but not yet visible in a read-back — monday indexes a new asset a
        // moment later, so this is a "give it a few seconds" note, not a failure.
        if (outcome.placement === 'column' && !outcome.verified) unconfirmed.push(target.label);
        // The column can refuse a write even when the item itself is writable; in that case the file
        // lands on the item's update feed rather than being lost.
        if (outcome.placement === 'update') relocatedUploads.push(target.label);
        if (outcome.placement === 'failed') {
          failedUploads.push(`${target.label} — ${outcome.reason}`);
          stillMissing.push(target);
        }
      }
      setPending(stillMissing);

      const sectionWarnings = res?.warnings ?? [];

      const code = makeReference();
      setReference(code);
      setItemId(newItemId);
      api.clearDraftStorage();
      setMode('submitted');
      window.scrollTo({ top: 0, behavior: 'smooth' });

      if (relocatedUploads.length > 0 && failedUploads.length === 0 && sectionWarnings.length === 0) {
        toast.success('הטופס הוגש ונשמר בלוח', {
          description: `אסמכתא ${code}. ${relocatedUploads.join(' ו')} צורפו לאזור העדכונים של הפריט.`,
          duration: 12000,
        });
      } else if (failedUploads.length > 0 || sectionWarnings.length > 0) {
        const allIssues = [...sectionWarnings, ...failedUploads];
        const manualHint = failedUploads.length > 0 ? ' יש לצרף את הקבצים ידנית לפריט בלוח.' : '';
        setSubmitError(`הטופס נשמר (אסמכתא ${code}), אך לא נקלטו: ${allIssues.join(' | ')}.${manualHint}`);
        toast.warning('הטופס נשמר בלוח, אך חלק מהפרטים לא נקלטו', {
          description: `${allIssues.join(' | ')}${manualHint}`,
          duration: 15000,
        });
      } else if (unconfirmed.length > 0) {
        toast.success('הטופס הוגש ונשמר בלוח', {
          description: `אסמכתא ${code}. ${unconfirmed.join(' ו')} נשלחו לפריט — ייתכן שיחלפו מספר שניות עד שיוצגו בלוח.`,
          duration: 10000,
        });
      } else {
        toast.success('הטופס הוגש ונשמר בלוח', { description: `אסמכתא ${code}` });
      }

      void reconcile(targets, newItemId);
    } catch (error: unknown) {
      console.error('Failed to submit declaration', error);
      const rawMsg = error instanceof Error ? error.message : String(error);
      let userFriendlyMsg = 'אירעה שגיאה בשליחת הטופס. אנא נסו שוב בעוד מספר רגעים.';

      if (rawMsg.includes('אימות אבטחה') || rawMsg.includes('Turnstile') || rawMsg.includes('אנושי') || rawMsg.includes('רובוט')) {
        userFriendlyMsg = 'אימות האבטחה פג תוקף או שלא הושלם. אנא סמנו מחדש את ריבוע האימות ("אני לא רובוט") ושלחו שוב.';
      } else if (rawMsg.includes('Rate limit') || rawMsg.includes('הגבלת בקשות') || rawMsg.includes('מרובות') || rawMsg.includes('ברצף')) {
        userFriendlyMsg = 'נשלחו מספר בקשות ברצף. אנא המתינו כדקה ונסו לשלוח שוב.';
      } else if (rawMsg.includes('3010') || rawMsg.includes('קובץ') || rawMsg.includes('סוג קובץ') || rawMsg.includes('נפח') || rawMsg.includes('10MB')) {
        userFriendlyMsg = 'קובץ טופס 3010 אינו תקין או שחורג מ-10MB. יש לוודא שהקובץ בפורמט PDF או PNG תקני.';
      } else if (rawMsg.includes('תעודת זהות') || rawMsg.includes('ספרת ביקורת')) {
        userFriendlyMsg = 'מספר תעודת הזהות אינו תקין. אנא ודאו את תקינות המספר שהוזן.';
      } else if (rawMsg.includes('דואר אלקטרוני') || rawMsg.includes('מייל') || rawMsg.includes('email')) {
        userFriendlyMsg = 'כתובת הדוא"ל שהוזנה אינה תקינה. יש לתקן אותה ולנסות שוב.';
      } else if (rawMsg.length > 5 && !rawMsg.includes('fetch') && !rawMsg.includes('NetworkError') && !rawMsg.includes('500') && !rawMsg.includes('Failed to submit')) {
        userFriendlyMsg = rawMsg;
      }

      setSubmitError(userFriendlyMsg);
      toast.error('שליחת הטופס נכשלה', {
        description: userFriendlyMsg,
        duration: 12000,
      });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setSubmitting(false);
    }
  };

  const reconcile = async (targets: UploadTarget[], targetItemId: string) => {
    if (targets.length === 0) return;
    setChecking(true);
    let remaining = targets;
    try {
      for (let round = 1; round <= 3 && remaining.length > 0; round += 1) {
        await new Promise((resolve) => setTimeout(resolve, round === 1 ? 6_000 : 8_000));
        const state = await checkDeclarationFiles({ data: { itemId: targetItemId } });
        remaining = remaining.filter((target) => (target.kind === 'signature' ? !state?.signature : !state?.form3010));
        if (remaining.length === 0) break;
        for (const target of remaining) await sendAttachment(target, targetItemId);
      }
    } catch (err) {
      console.error('Attachment verification failed', err);
    }
    setChecking(false);
    setPending(remaining);
    if (remaining.length === 0) {
      setSubmitError(null);
      return;
    }
    const labels = remaining.map((target) => target.label).join(' ו');
    setSubmitError(`${labels} לא אומתו בעמודת הקובץ בלוח. ניתן לשלוח שוב מכאן.`);
    toast.warning('קובץ לא אומת בלוח', { description: `${labels} — ניתן לנסות לצרף שוב.`, duration: 12000 });
  };

  const retryAttachments = async () => {
    if (!itemId || pending.length === 0) return;
    setRetrying(true);
    const attached: string[] = [];
    const failed: string[] = [];
    const left: UploadTarget[] = [];
    for (const target of pending) {
      const outcome = await sendAttachment(target, itemId);
      if (outcome.placement === 'failed') {
        failed.push(`${target.label} — ${outcome.reason}`);
        left.push(target);
      } else {
        attached.push(target.label);
      }
    }
    setPending(left);
    setRetrying(false);
    if (attached.length > 0) toast.success(`צורף בהצלחה: ${attached.join(', ')}`);
    if (failed.length > 0) {
      setSubmitError(`הצירוף נכשל שוב: ${failed.join(' | ')}`);
      toast.error('הצירוף נכשל שוב');
    } else {
      setSubmitError(null);
    }
  };

  const startNew = () => {
    api.reset();
    setForm3010Files([]);
    setPending([]);
    setReference('');
    setItemId(null);
    setMode('edit');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (mode === 'submitted') {
    return (
      <div className="px-4 py-8 sm:px-6 lg:py-12">
        {submitError ? (
          <div
            role="alert"
            className="mx-auto mb-5 max-w-[900px] border border-destructive bg-[hsl(var(--surface-1))] px-4 py-3 text-sm leading-relaxed text-foreground"
          >
            {submitError}
          </div>
        ) : null}
        <AttachmentRetry
          labels={pending.map((target) => target.label)}
          busy={retrying}
          checking={checking}
          onRetry={retryAttachments}
        />
        <SubmittedReceipt values={api.values} reference={reference} itemId={itemId} onNewForm={startNew} />
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-[1180px] px-4 py-8 sm:px-6 lg:py-12">
      <nav aria-label="מסלול ניווט" className="no-print mb-6 flex items-center justify-end gap-1.5 text-xs text-muted-foreground">
        <Link to="/" className="inline-flex items-center gap-1.5 hover:text-foreground">
          חזרה לטפסים
          <ArrowRight className="size-3.5" aria-hidden="true" />
        </Link>
      </nav>

      <header className="no-print mb-6 border border-[hsl(var(--rule))] bg-[hsl(var(--surface-1))] px-6 py-10 text-center sm:px-10">
        <p className="text-[11px] font-medium tracking-[0.16em] text-muted-foreground">{academicYearTitle()}</p>
        <h1 className="mt-3 text-2xl font-bold leading-tight text-foreground sm:text-3xl">טופס הצהרת מילואים</h1>
        <p className="mt-2 text-sm text-muted-foreground">מיועד לעובדי הפקולטה להנדסה</p>
      </header>

      <dl className="no-print mb-6 grid gap-px overflow-hidden border border-[hsl(var(--rule))] bg-[hsl(var(--rule-soft))] sm:grid-cols-3">
        {[
          { icon: <Clock3 className="size-4" aria-hidden="true" />, label: 'זמן מילוי משוער', value: 'כ-7 דקות' },
          { icon: <PenLine className="size-4" aria-hidden="true" />, label: 'נדרשת חתימה', value: 'חתימה ידנית בטופס' },
          { icon: <Files className="size-4" aria-hidden="true" />, label: 'מסמכים נדרשים', value: 'עד 4 קבצי 3010 / אישור צבאי' },
        ].map((item) => (
          <div key={item.label} className="bg-[hsl(var(--surface-1))] px-4 py-3">
            <dt className="flex items-center gap-2 text-[11px] font-medium tracking-[0.1em] text-muted-foreground">
              {item.icon}
              {item.label}
            </dt>
            <dd className="mt-1 text-sm text-foreground">{item.value}</dd>
          </div>
        ))}
      </dl>

      {mode === 'edit' ? (
        <DeclarationEditor
          api={api}
          onContinue={goToReview}
          onForm3010Selected={setForm3010Files}
          form3010Files={form3010Files}
        />
      ) : (
        <div className="mx-auto max-w-[900px]">
          <div className="no-print mb-5 border border-[hsl(var(--rule))] bg-[hsl(var(--tint-sky))] px-4 py-3 text-sm leading-relaxed text-foreground">
            זהו סיכום הטופס לפני ההגשה. יש לעבור על הפרטים, ובמידת הצורך לחזור לעריכה. ההגשה יוצרת פריט בלוח הפקולטה.
          </div>
          <div className="border border-[hsl(var(--rule))] bg-[hsl(var(--surface-1))]">
            <DeclarationDocument values={api.values} />
          </div>

          <div className="mt-5">
            <TurnstileWidget
              onVerify={(token) => setTurnstileToken(token)}
              onExpire={() => setTurnstileToken(null)}
            />
          </div>

          {submitError ? <p className="mt-3 text-sm font-medium text-destructive">{submitError}</p> : null}
          <ActionBar
            status={
              turnstileToken ? (
                <span className="flex items-center gap-1.5 font-semibold text-emerald-700 dark:text-emerald-400 text-xs sm:text-sm">
                  <CheckCircle2 className="size-4 shrink-0" />
                  אימות האבטחה הושלם — הטופס מוכן לשליחה.
                </span>
              ) : (
                <span className="flex items-center gap-1.5 font-medium text-amber-800 dark:text-amber-200 bg-amber-500/10 px-2.5 py-1 rounded border border-amber-500/30 text-xs">
                  <AlertCircle className="size-3.5 shrink-0 text-amber-600 dark:text-amber-400" />
                  חובה לסמן את ריבוע האימות ("אני לא רובוט") למעלה כדי לשלוח את הטופס
                </span>
              )
            }
          >
            <BarButton tone="ghost" onClick={() => setMode('edit')} disabled={submitting}>
              חזרה לעריכה
            </BarButton>
            <BarButton
              tone="primary"
              onClick={() => {
                if (!turnstileToken) {
                  const el = document.getElementById('human-verification-widget');
                  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                  toast.error('חובה לסמן את ריבוע האימות "אני לא רובוט" לפני שליחת הטופס.');
                  return;
                }
                submit();
              }}
              disabled={submitting}
              className={!turnstileToken ? 'opacity-60 cursor-not-allowed' : ''}
            >
              <Check className="size-4" aria-hidden="true" />
              {submitting ? 'שולח…' : 'שליחת הטופס'}
            </BarButton>
          </ActionBar>
        </div>
      )}
    </div>
  );
}
